import api from "./apiMinty";
import TokenService from "./token.service";
import { localStorageName } from "@/config/utils";

// Member-auth methods ported from the member portal (auth.service.js).
// All calls go through apiMinty (CRM api), mirroring the member portal's
// endpoints and request bodies exactly.
class MemberAuthService {
  validatePhoneNumber(phone_number, phone_code) {
    return api.post("member/validate-phone", { phone_number, phone_code });
  }

  sendOtp({ phone_number, is_profile, invite_code, i_agree, phone_code }) {
    return api
      .post("member/send-otp", {
        phone_number,
        is_profile,
        invite_code,
        i_agree,
        phone_code,
      })
      .then((response) => {
        return response.data;
      });
  }

  loginOtp({ phone_number, otp, i_agree, passcode = "", is_empty_passcode = false, phone_code }) {
    return api
      .post("member/login-otp", {
        phone_number,
        otp,
        i_agree,
        passcode,
        is_empty_passcode,
        phone_code,
      })
      .then((response) => {
        if (response.data.result.isSuccess) {
          TokenService.setMember(response.data);
          // Store in the shape the MOO app checks for login state
          localStorage.setItem(localStorageName("token"), response.data.data.authorisation.token);
          localStorage.setItem(localStorageName("memberInfo"), JSON.stringify(response.data.data.user));
        }
        return response.data;
      });
  }

  validateOtp({ phone_number, otp }) {
    return api
      .post("member/validate-otp", {
        phone_number,
        otp,
      })
      .then((response) => {
        return response.data;
      });
  }

  // SH003: create a salon customer's profile and sign them in, in one call.
  salonRegister({ name, email, phone_number, phone_code, passcode, i_agree }) {
    return api
      .post("member/salon/register", {
        name,
        email,
        phone_number,
        phone_code,
        passcode,
        i_agree,
      })
      .then((response) => {
        if (response.data.result.isSuccess) {
          // Same bookkeeping loginOtp() does, so the app treats the new member as
          // logged in rather than half-registered.
          TokenService.setMember(response.data);
          localStorage.setItem(localStorageName("token"), response.data.data.authorisation.token);
          localStorage.setItem(localStorageName("memberInfo"), JSON.stringify(response.data.data.user));
        }
        return response.data;
      });
  }

  // SH002: everything the salon "Welcome back" card renders, in one call.
  getSalonProfileSummary() {
    return api.post("member/salon/profile-summary", {});
  }

  // SH024: the member record behind the salon profile screen. MOO's own Profile.vue
  // reads the same endpoint through member.service.js, but that one takes the merchant
  // code as an argument and builds an absolute URL; apiMinty already carries the token
  // and both merchant headers, the way the rest of the salon flow calls the CRM api.
  getMemberProfile() {
    return api.post("member/get-data-member-moo", {});
  }

  // SH024: every field is optional server-side, so only send what the screen edits —
  // phone_number and phone_code are read-only there and are deliberately left out.
  //
  // An empty date_of_birth is dropped rather than sent: the endpoint writes whatever it
  // is given as long as the key is present, so posting "" would blank a date of birth
  // the member had not touched.
  updateMemberProfile({ name, last_name, email, date_of_birth }) {
    const payload = { name, last_name, email };

    if (date_of_birth) {
      payload.date_of_birth = date_of_birth;
    }

    return api.post("member/update-data-member-moo", payload);
  }

  checkTokenHasExpired() {
    return api.get("member/check-token-has-expired");
  }

  sendLinkForgotPasscode(phone_number, link) {
    return api.post("member/send-link-forgot-passcode", { phone_number, link });
  }

  sendResetToInitialPasscode(phone_number, phone_code) {
    return api.post("member/send-reset-to-initial-passcode", { phone_number, phone_code });
  }

  getOneTimePassCode(phone_number, phone_code) {
    return api.post("member/get-one-time-passcode", { phone_number, phone_code });
  }

  changePasscode({ passcode, phone_number }) {
    return api
      .post("member/change-passcode", {
        phone_number,
        passcode,
      })
      .then((response) => {
        return response.data;
      });
  }

  updatePasscode(reset_pass_code, new_passcode) {
    return api.post("member/update-passcode", { reset_pass_code, new_passcode });
  }

  validateLinkForgetPasscodeByCode(reset_pass_code) {
    return api.post("member/validate-link-forget-passcode-by-code", { reset_pass_code });
  }
}

export default new MemberAuthService();
